import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { UploadOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Modal, Row, Select, Upload } from "antd";
import { axiosErrorHandler, displayImageUploadErrorMessage } from "../../../../../swzutils";
import InsuranceCategory from "../../../enums/InsuranceCategory";
import {} from "../api/Representation";
import { UploadFileTransformer } from "../../../api/Representation";
import API from "../../../../../API";
import LocalDatePicker from "../../../components/LocalDatePicker";
const InsurancePolicyModal = ({ open, tenantId, insurancePolicy, onPolicyAddedOrUpdated, onCancel }) => {
    const [loading, setLoading] = useState(false);
    const [uploadedFileIds, setUploadedFileIds] = useState([]);
    const [removedDocumentIds, setRemovedDocumentIds] = useState([]);
    const [form] = Form.useForm();
    useEffect(() => {
        if (open) {
            setLoading(false);
            setUploadedFileIds([]);
            setRemovedDocumentIds([]);
            if (insurancePolicy) {
                form.setFieldsValue(insurancePolicy);
            }
            else {
                form.resetFields();
            }
        }
    }, [open]);
    const onFinish = (formValues) => {
        setLoading(true);
        const request = {
            policyNumber: formValues.policyNumber,
            description: formValues.description,
            insurerName: formValues.insurerName,
            category: formValues.category,
            expiryDate: formValues.expiryDate,
            uploadedFileIds: uploadedFileIds,
            removedDocumentIds: removedDocumentIds,
            version: insurancePolicy ? insurancePolicy.version : undefined
        };
        const axiosRequest = insurancePolicy
            ? axios.put(`/admin/v1/tenants/${tenantId}/insurance-policies/${insurancePolicy.id}`, request)
            : axios.post(`/admin/v1/tenants/${tenantId}/insurance-policies`, request);
        axiosRequest
            .then(response => onPolicyAddedOrUpdated(response.data))
            .catch(axiosErrorHandler)
            .finally(() => setLoading(false));
    };
    const onAttachedDocumentUploaded = (uploadedFile) => {
        const uploadedFileIdsSet = new Set(uploadedFileIds);
        uploadedFileIdsSet.add(uploadedFile.id);
        setUploadedFileIds([...uploadedFileIdsSet]);
    };
    const onAttachedDocumentRemoved = (removedFile) => {
        if (removedFile.response && removedFile.response.deleteUri) {
            // Uploaded in this session so delete the uploaded file on the server
            // Best effort delete.  If this fails there is a scheduled task that will clean it up
            axios.delete(`${API.origin()}${removedFile.response.deleteUri}`);
        }
        else if (removedFile.response?.id) {
            // Add the id to the list of removed files it will be removed on save
            const removedFileIdsSet = new Set(removedDocumentIds);
            removedFileIdsSet.add(removedFile.response.id);
            setRemovedDocumentIds([...removedFileIdsSet]);
        }
    };
    const fileList = useMemo(() => {
        if (insurancePolicy?.documents?.length) {
            return insurancePolicy.documents.map(doc => {
                let contentsUri = `${API.origin()}/admin/v1/tenants/${tenantId}/insurance-policies/${insurancePolicy.id}/documents/${doc.id}/contents`;
                return UploadFileTransformer.fromDocumentResponse(doc, contentsUri);
            });
        }
        return [];
    }, [insurancePolicy]);
    const editMode = !!insurancePolicy;
    return (_jsx(Modal, { onOk: form.submit, onCancel: onCancel, destroyOnHidden: true, confirmLoading: loading, open: open, title: editMode ? "Edit policy" : "Add policy", children: _jsxs(Form, { layout: "vertical", form: form, onFinish: onFinish, children: [_jsx(Form.Item, { name: "policyNumber", label: "Policy Number", initialValue: insurancePolicy && insurancePolicy.policyNumber, rules: [
                        { required: true, message: 'Please enter an policy number.' },
                        { max: 50, message: 'Please limit the policy number to 50 characters.' }
                    ], children: _jsx(Input, {}) }), _jsx(Form.Item, { name: "description", label: "Description", initialValue: insurancePolicy && insurancePolicy.description, rules: [
                        { required: true, message: 'Please enter a description.' },
                        { max: 250, message: 'Please limit the description to 250 characters.' }
                    ], children: _jsx(Input, {}) }), _jsx(Form.Item, { name: "insurerName", initialValue: insurancePolicy && insurancePolicy.insurerName, label: "Insurer", rules: [
                        { max: 50, message: 'Please limit the insurer name to 50 characters.' }
                    ], children: _jsx(Input, {}) }), _jsxs(Row, { gutter: 16, children: [_jsx(Col, { className: "gutter-row", span: 12, children: _jsx(Form.Item, { name: "category", label: "Category", initialValue: insurancePolicy && insurancePolicy.category, rules: [
                                    { required: true, message: 'Please select a category.' }
                                ], children: _jsx(Select, { showSearch: {
                                        optionFilterProp: ['label']
                                    }, options: InsuranceCategory.selectOptionsArray() }) }) }), _jsx(Col, { span: 12, children: _jsx(Form.Item, { name: "expiryDate", label: "Expiry Date", initialValue: insurancePolicy && insurancePolicy.expiryDate, rules: [
                                    { required: true, message: 'Please enter an expiry date.' }
                                ], children: _jsx(LocalDatePicker, { format: "DD/MM/YYYY", allowClear: false }) }) })] }), _jsx(Upload, { name: "uploadFile", accept: ".pdf,.png,.gif,.jpg", listType: "picture", defaultFileList: fileList, withCredentials: true, action: `${API.origin()}/admin/v1/files`, onChange: (info) => {
                        const status = info.file.status;
                        //console.log("status:" + status + ".  info", info);
                        if (status === 'done' && info.file?.response) {
                            onAttachedDocumentUploaded(info.file.response);
                        }
                        else if (status === 'error') {
                            displayImageUploadErrorMessage(info.file);
                        }
                    }, onRemove: onAttachedDocumentRemoved, children: _jsx(Button, { icon: _jsx(UploadOutlined, {}), children: "Upload Certificate of Currency" }) })] }) }));
};
export default InsurancePolicyModal;
